import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { insertPatientMedication } from '../data/mockPatientMedications';

/* ── Common antihypertensive medications for dropdown ── */
const COMMON_MEDICATIONS = [
  { name: 'Lisinopril',     class: 'ace-inhibitor',      strengths: ['5mg', '10mg', '20mg', '40mg'],       form: 'Tablet' },
  { name: 'Amlodipine',     class: 'ccb',                strengths: ['2.5mg', '5mg', '10mg'],              form: 'Tablet' },
  { name: 'Losartan',       class: 'arb',                strengths: ['25mg', '50mg', '100mg'],             form: 'Tablet' },
  { name: 'HCTZ',           class: 'thiazide',           strengths: ['12.5mg', '25mg', '50mg'],            form: 'Tablet' },
  { name: 'Metoprolol',     class: 'beta-blocker',       strengths: ['25mg', '50mg', '100mg', '200mg'],    form: 'Tablet' },
  { name: 'Valsartan',      class: 'arb',                strengths: ['40mg', '80mg', '160mg', '320mg'],    form: 'Tablet' },
  { name: 'Enalapril',      class: 'ace-inhibitor',      strengths: ['2.5mg', '5mg', '10mg', '20mg'],      form: 'Tablet' },
  { name: 'Spironolactone', class: 'k-sparing-diuretic', strengths: ['25mg', '50mg', '100mg'],             form: 'Tablet' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'initiation',    label: 'Initiation — new drug' },
  { value: 'dose-increase', label: 'Dose increase' },
  { value: 'dose-decrease', label: 'Dose decrease' },
  { value: 'renewal',       label: 'Renewal — same dose' },
  { value: 'discontinuation', label: 'Discontinuation' },
];

/**
 * Renders the prescriber's cursive signature to a canvas and returns a PNG data-URL
 * for embedding in generated PDFs.
 */
function getSignatureDataURL() {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 72;
  const ctx = canvas.getContext('2d');

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Cursive name in dark-blue ink
  ctx.fillStyle = 'rgba(20, 38, 110, 0.92)';
  ctx.font = 'italic 36px Georgia, "Times New Roman", serif';
  ctx.fillText('Doctor Tarzan', 8, 46);

  // Underline flourish
  ctx.strokeStyle = 'rgba(20, 38, 110, 0.85)';
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(8, 55);
  ctx.bezierCurveTo(75, 53, 148, 60, 232, 57);
  ctx.bezierCurveTo(254, 56, 264, 52, 268, 49);
  ctx.stroke();

  return canvas.toDataURL('image/png');
}

/* ── Hardcoded prescriber info ── */
const PRESCRIBER = {
  name: 'Doctor Tarzan',
  credentials: 'PharmD',
  npi: '1234567890',
  address: '123 Withings Health Blvd, Suite 400',
  city: 'Boston, MA 02110',
  phone: '(617) 555-0199',
  dea: '', // Not needed for non-controlled
};

/**
 * Guess the next logical prescription from the patient's medication rows.
 * Uses structured drug_name + dose fields — no label string-parsing.
 */
function suggestMedication(medications) {
  if (!medications || medications.length === 0) {
    return { medIndex: 0, strengthIndex: 0, eventType: 'initiation' };
  }

  // Find latest row per drug, filter out discontinued drugs
  const latestByDrug = {};
  for (const row of medications) {
    const existing = latestByDrug[row.drug_name];
    if (!existing || row.prescribed_at > existing.prescribed_at) {
      latestByDrug[row.drug_name] = row;
    }
  }
  const activeMeds = Object.values(latestByDrug).filter(r => r.event_type !== 'discontinuation');

  if (activeMeds.length === 0) {
    return { medIndex: 0, strengthIndex: 0, eventType: 'initiation' };
  }

  // Most recently prescribed active drug
  const lastMed = activeMeds.reduce((a, b) => (a.prescribed_at > b.prescribed_at ? a : b));

  // Match against catalog
  for (let i = 0; i < COMMON_MEDICATIONS.length; i++) {
    const med = COMMON_MEDICATIONS[i];
    if (med.name.toLowerCase() === lastMed.drug_name.toLowerCase()) {
      const currentStrIdx = med.strengths.findIndex(
        s => s.toLowerCase() === lastMed.dose.toLowerCase()
      );
      if (currentStrIdx === -1) return { medIndex: i, strengthIndex: 0, eventType: 'renewal' };
      const nextStrIdx = Math.min(currentStrIdx + 1, med.strengths.length - 1);
      const eventType = nextStrIdx > currentStrIdx ? 'dose-increase' : 'renewal';
      return { medIndex: i, strengthIndex: nextStrIdx, eventType };
    }
  }

  return { medIndex: 0, strengthIndex: 0, eventType: 'initiation' };
}

export default function PrescriptionModal({ patient, medications, onSaved, onClose }) {
  const suggestion = useMemo(() => suggestMedication(medications), [medications]);

  // Form state
  const [medIndex, setMedIndex]       = useState(suggestion.medIndex);
  const [strength, setStrength]       = useState(COMMON_MEDICATIONS[suggestion.medIndex].strengths[suggestion.strengthIndex]);
  const [eventType, setEventType]     = useState(suggestion.eventType);
  const [quantity, setQuantity]       = useState('30');
  const [sig, setSig]                 = useState('Take 1 tablet by mouth once daily');
  const [refills, setRefills]         = useState('3');
  const [notes, setNotes]             = useState('');
  const [generated, setGenerated]     = useState(false);

  const selectedMed = COMMON_MEDICATIONS[medIndex];

  // Update strength when medication changes
  useEffect(() => {
    setStrength(COMMON_MEDICATIONS[medIndex].strengths[0]);
  }, [medIndex]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  async function generatePDF() {
    // Lazy-load jsPDF (~300 KB) — only fetched when the user actually
    // prints a prescription.
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'letter' });
    const w = doc.internal.pageSize.getWidth();
    let y = 20;

    // ── Header ──
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('PRESCRIPTION', w / 2, y, { align: 'center' });
    y += 8;

    // Prescriber block
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.setFont(undefined, 'bold');
    doc.text(PRESCRIBER.name + ', ' + PRESCRIBER.credentials, 20, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(PRESCRIBER.address, 20, y); y += 5;
    doc.text(PRESCRIBER.city, 20, y); y += 5;
    doc.text(`Phone: ${PRESCRIBER.phone}   |   NPI: ${PRESCRIBER.npi}`, 20, y);
    y += 4;

    // Line
    doc.setDrawColor(200);
    doc.line(20, y, w - 20, y);
    y += 8;

    // Date
    doc.setFontSize(10);
    doc.text(`Date: ${today}`, 20, y);
    y += 10;

    // ── Patient info ──
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('PATIENT', 20, y); y += 6;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${patient.firstname} ${patient.lastname}`, 20, y); y += 5;
    doc.text(`User ID: ${patient.userid}`, 20, y); y += 5;
    doc.text(`Last BP Reading: ${patient.lastSystolic || '—'} mmHg`, 20, y);
    y += 10;

    // Line
    doc.line(20, y, w - 20, y);
    y += 8;

    // ── Rx ──
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(22, 56, 166); // Withings blue
    doc.text('Rx', 20, y); y += 8;

    doc.setFontSize(13);
    doc.setTextColor(0);
    doc.text(`${selectedMed.name} ${strength} ${selectedMed.form}`, 20, y); y += 7;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Sig: ${sig}`, 20, y); y += 6;
    doc.text(`Quantity: ${quantity}`, 20, y); y += 6;
    doc.text(`Refills: ${refills}`, 20, y); y += 6;

    if (notes.trim()) {
      y += 4;
      doc.text(`Notes: ${notes}`, 20, y); y += 6;
    }

    y += 8;
    doc.line(20, y, w - 20, y);
    y += 12;

    // ── Signature ──
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text('Prescriber signature:', 20, y); y += 4;
    const sigDataUrl = getSignatureDataURL();
    doc.addImage(sigDataUrl, 'PNG', 20, y, 85, 19); // 85 mm × 19 mm
    y += 22;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`${PRESCRIBER.name}, ${PRESCRIBER.credentials}`, 20, y);

    // ── Footer ──
    const fy = doc.internal.pageSize.getHeight() - 15;
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('Generated by ACCESS Dashboard — Withings RPM', w / 2, fy, { align: 'center' });
    doc.text('This prescription is for demonstration purposes only.', w / 2, fy + 4, { align: 'center' });

    // Save
    const filename = `Rx_${patient.firstname}_${patient.lastname}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  }

  async function handleGenerate() {
    await generatePDF();

    // Persist to mock store (replace insertPatientMedication with the Platform API write when ready)
    const newRow = await insertPatientMedication(patient.userid, {
      medication_class: selectedMed.class,
      drug_name: selectedMed.name.toLowerCase(),
      brand_name: null,
      form: selectedMed.form.toLowerCase(),
      dose: strength,
      frequency: 'daily',
      route: 'oral',
      sig,
      quantity: parseInt(quantity, 10),
      refills: parseInt(refills, 10),
      event_type: eventType,
      discontinue_reason: null,
      prescribed_by: `${PRESCRIBER.name}, ${PRESCRIBER.credentials}`,
      prescriber_npi: PRESCRIBER.npi,
      pharmacist_notes: notes.trim() || null,
      source: 'pharmacist',
      prescribed_at: new Date().toISOString().slice(0, 10),
      started_at: null,
      care_period_id: null,
    });

    setGenerated(true);
    onSaved?.(newRow);
  }

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content prescription-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="header3" style={{ margin: 0 }}>
              New Prescription
            </h2>
            <p className="detail1 text-secondary" style={{ margin: '4px 0 0', textTransform: 'capitalize' }}>
              {patient.firstname} {patient.lastname} · Last BP: {patient.lastSystolic || '—'} mmHg
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Two-column layout: prescriber + patient */}
        <div className="rx-info-row">
          <div className="rx-info-block">
            <span className="eyebrow text-secondary">Prescriber</span>
            <p className="body2" style={{ margin: '4px 0 0', fontWeight: 500 }}>{PRESCRIBER.name}, {PRESCRIBER.credentials}</p>
            <p className="detail1 text-secondary" style={{ margin: '2px 0 0' }}>NPI: {PRESCRIBER.npi}</p>
          </div>
          <div className="rx-info-block">
            <span className="eyebrow text-secondary">Patient</span>
            <p className="body2" style={{ margin: '4px 0 0', fontWeight: 500, textTransform: 'capitalize' }}>{patient.firstname} {patient.lastname}</p>
            <p className="detail1 text-secondary" style={{ margin: '2px 0 0' }}>ID: {patient.userid}</p>
          </div>
          <div className="rx-info-block">
            <span className="eyebrow text-secondary">Date</span>
            <p className="body2" style={{ margin: '4px 0 0', fontWeight: 500 }}>{today}</p>
          </div>
        </div>

        {/* Medication form */}
        <div className="rx-form">
          <div className="rx-form-row">
            <div className="rx-field" style={{ flex: 2 }}>
              <label className="eyebrow text-secondary">Medication</label>
              <select
                className="rx-select"
                value={medIndex}
                onChange={e => setMedIndex(Number(e.target.value))}
              >
                {COMMON_MEDICATIONS.map((m, i) => (
                  <option key={m.name} value={i}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="rx-field" style={{ flex: 1 }}>
              <label className="eyebrow text-secondary">Strength</label>
              <select
                className="rx-select"
                value={strength}
                onChange={e => setStrength(e.target.value)}
              >
                {selectedMed.strengths.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="rx-field" style={{ flex: 1 }}>
              <label className="eyebrow text-secondary">Form</label>
              <input className="rx-input" value={selectedMed.form} readOnly />
            </div>
          </div>

          <div className="rx-form-row">
            <div className="rx-field" style={{ flex: 1 }}>
              <label className="eyebrow text-secondary">Event type</label>
              <select
                className="rx-select"
                value={eventType}
                onChange={e => setEventType(e.target.value)}
              >
                {EVENT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="rx-field" style={{ flex: 3 }}>
              <label className="eyebrow text-secondary">Directions (Sig)</label>
              <input
                className="rx-input"
                value={sig}
                onChange={e => setSig(e.target.value)}
                placeholder="Take 1 tablet by mouth once daily"
              />
            </div>
          </div>

          <div className="rx-form-row">
            <div className="rx-field" style={{ flex: 1 }}>
              <label className="eyebrow text-secondary">Quantity</label>
              <input
                className="rx-input"
                type="number"
                min="1"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
              />
            </div>
            <div className="rx-field" style={{ flex: 1 }}>
              <label className="eyebrow text-secondary">Refills</label>
              <input
                className="rx-input"
                type="number"
                min="0"
                max="12"
                value={refills}
                onChange={e => setRefills(e.target.value)}
              />
            </div>
            <div className="rx-field" style={{ flex: 2 }}>
              <label className="eyebrow text-secondary">Additional notes</label>
              <input
                className="rx-input"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="rx-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleGenerate}>
            {generated ? '✓ Download again' : 'Generate Prescription PDF'}
          </button>
          <button className="btn-primary" disabled style={{ opacity: 0.5 }} title="Coming soon — DoseSpot integration">
            Send to pharmacy
          </button>
        </div>

        {generated && (
          <div className="cms-success-toast" style={{ marginTop: 16 }}>
            Prescription PDF downloaded — ready to send to EHR for signature.
          </div>
        )}
      </div>
    </div>
  ), document.body);
}
